import moment from "moment";

import { DATETIME_FORMAT_WITH_T } from "constants.js";
import * as config from "util/config";
import * as i18n from "util/i18n";
import * as numUtils from "util/number";

export function formatMovementValueDate(fechaValor) {
    const raw = String(fechaValor ?? "");
    const date = raw.substring(0, raw.indexOf("T"));
    return moment(date).format("DD/MM/YYYY");
}

export function mapMovementToCardRow(movement, currencyIso, hideAmounts) {
    return {
        id: movement.identificador,
        formattedValueDate: formatMovementValueDate(movement.fechaValor),
        voucherLabel: i18n.get("accounts.pendingMovements.table.header.voucher"),
        voucherNumber: movement.comprobante,
        concept: movement.concepto,
        transactionDetail: movement.detalleTransaccion,
        amount: numUtils.parseDoubleWithSymbol(movement.importe, movement.signoImporte),
        partialBalanceAmount: numUtils.parseDoubleWithSymbol(movement.saldoParcial, movement.signoSdoParcial),
        currency: currencyIso,
        partialBalanceLabel: i18n.get("accounts.lastestMovements.table.header.partialBalance"),
        hideAmounts,
        uppercasePartialBalanceLabel: true,
    };
}

export function getPendingMinDate() {
    const isTest = config.getBoolean("corePendingDate.isTest", false);
    const strDate = config.get("coreDate.defaultPendingDate");
    return isTest && strDate ? moment(strDate, DATETIME_FORMAT_WITH_T) : moment().startOf("day");
}

function criterionFromSearchBy(raw) {
    if (raw == null) {
        return "period";
    }
    if (typeof raw === "string") {
        if (raw === "all") {
            return "amount";
        }
        if (raw === "operations") {
            return "movementType";
        }
        return raw;
    }
    const { value } = raw;
    if (value === "all") {
        return "amount";
    }
    if (value === "operations") {
        return "movementType";
    }
    return value || "period";
}

export function resolveFindByFromFormValues(formValues = {}) {
    if (formValues.searchBy != null) {
        return criterionFromSearchBy(formValues.searchBy);
    }
    if (formValues.findBy != null) {
        const raw = formValues.findBy;
        const value = typeof raw === "object" ? raw.value : raw;
        if (value === "operations") {
            return "movementType";
        }
        return value || "period";
    }
    return "period";
}

export function resolveMovementTypeFromFormValues(formValues = {}) {
    const raw = formValues.movementType ?? formValues.operations;
    if (raw == null) {
        return null;
    }
    const value = typeof raw === "object" ? raw.value : raw;
    if (value === "all" || value === 2 || value === "2") {
        return 2;
    }
    if (value === "credit" || value === 1 || value === "1") {
        return 1;
    }
    if (value === "debit" || value === 0 || value === "0") {
        return 0;
    }
    return typeof value === "number" ? value : null;
}

export function buildLatestMovementSearchByOptions(getLabels) {
    return [
        { value: "period", label: getLabels("period") },
        { value: "amount", label: getLabels("amount") },
        { value: "detail", label: getLabels("detail") },
        { value: "voucher", label: getLabels("voucher") },
        { value: "longDetail", label: getLabels("longDetail") },
    ];
}

export function buildPendingMovementSearchByOptions(getLabels) {
    return [
        { value: "period", label: getLabels("period") },
        { value: "amount", label: getLabels("amount") },
        { value: "detail", label: getLabels("detail") },
        { value: "operations", label: getLabels("operations") },
    ];
}

export function buildMovementOperationsOptions(getLabels) {
    return [
        { value: 1, label: getLabels("movementTypeCredit") },
        { value: 0, label: getLabels("movementTypeDebit") },
        { value: 2, label: getLabels("movementTypeAll") },
    ];
}

export function buildInitialMovementsFormValues(getLabels) {
    const periodOption = { value: "period", label: getLabels("period") };
    return {
        movementScope: "latest",
        findBy: periodOption,
        searchBy: periodOption,
        movementType: null,
        amountFrom: null,
        amountTo: null,
        voucher: "",
        detail: "",
        longDetail: "",
        ...buildInitialMovementDates(),
    };
}

export function buildInitialMovementDates() {
    const configuredDateFromValue = config.getInteger("movements.dateTo.periodFront", 1);
    const startDateLatest = moment()
        .startOf("day")
        .subtract(configuredDateFromValue, "months");
    const endDateLatest = moment().startOf("day");
    const startDatePending = getPendingMinDate();
    const plusDays = config.getInteger("movements.maxDays.default", 30);
    const endDatePending = getPendingMinDate().add(plusDays, "days");
    const enableRangeDatefield = config.getBoolean("frontend.show.RangeDatePicker.functionalities", false);

    if (enableRangeDatefield) {
        return {
            findBy: "period",
            lastDates: [startDateLatest.toDate(), endDateLatest.toDate()],
            pendingDates: [startDatePending.toDate(), endDatePending.toDate()],
            lastDateFrom: null,
            lastDateTo: null,
            pendingDateFrom: null,
            pendingDateTo: null,
        };
    }

    return {
        findBy: "period",
        lastDateFrom: startDateLatest,
        lastDateTo: endDateLatest,
        pendingDateFrom: startDatePending,
        pendingDateTo: endDatePending,
        lastDates: undefined,
        pendingDates: undefined,
    };
}

export function formatAmountForApi(value) {
    if (value == null || value === "") {
        return "";
    }
    if (typeof value === "object" && value.amount != null && value.amount !== "") {
        return formatAmountForApi(value.amount);
    }
    let numeric;
    if (typeof value === "number") {
        numeric = value;
    } else if (typeof value === "string") {
        const normalized = value.replaceAll(".", "").replaceAll(",", ".");
        numeric = parseFloat(normalized);
    } else {
        return "";
    }
    return Number.isFinite(numeric) ? numeric.toFixed(2) : "";
}

function amountFieldForApi(value) {
    const formatted = formatAmountForApi(value);
    if (formatted === "") {
        return null;
    }
    return { amount: formatted };
}

export function buildMovementFilterPayload(findBy, formValues = {}) {
    const trimmedVoucher = formValues.voucher?.trim();
    const trimmedDetail = formValues.detail?.trim();
    const trimmedLongDetail = formValues.longDetail?.trim();
    const movementType = resolveMovementTypeFromFormValues(formValues);
    const type = movementType > 1 ? null : movementType;
    const inactiveFilterFields = {
        voucher: null,
        detail: null,
        type: null,
    };

    switch (findBy) {
        case "amount": {
            const payload = {
                ...inactiveFilterFields,
                amountTo: amountFieldForApi(formValues.amountTo),
            };
            const amountFrom = amountFieldForApi(formValues.amountFrom);
            if (amountFrom != null) {
                payload.amountFrom = amountFrom;
            }
            return payload;
        }
        case "voucher":
            return { ...inactiveFilterFields, voucher: trimmedVoucher || null };
        case "detail":
            return { ...inactiveFilterFields, detail: trimmedDetail || null };
        case "longDetail":
            return { ...inactiveFilterFields, longDetail: trimmedLongDetail || null };
        case "movementType":
            return { ...inactiveFilterFields, type: type ?? null };
        default:
            return { ...inactiveFilterFields };
    }
}

export function dispatchFetchLatestMovements(dispatch, accountsActions, params) {
    const {
        account,
        isDesktop,
        formValues = {},
        latestMovementsPageNumber = "1",
        pendingMovementsPageNumber = "1",
    } = params;

    const {
        lastDateFrom,
        lastDateTo,
        lastDates,
        pendingDateFrom,
        pendingDateTo,
        pendingDates,
        activeSection,
    } = formValues;

    const findBy = resolveFindByFromFormValues(formValues);
    const isLatestMovementsSelected = activeSection !== "pendingmovements";
    const defaultDates = buildInitialMovementDates();
    const filterPayload = buildMovementFilterPayload(findBy, formValues);

    const resolvedLastDates = lastDates ?? defaultDates.lastDates;
    const resolvedPendingDates = pendingDates ?? defaultDates.pendingDates;
    const resolvedLastDateFrom = lastDateFrom ?? defaultDates.lastDateFrom;
    const resolvedLastDateTo = lastDateTo ?? defaultDates.lastDateTo;
    const resolvedPendingDateFrom = pendingDateFrom ?? defaultDates.pendingDateFrom;
    const resolvedPendingDateTo = pendingDateTo ?? defaultDates.pendingDateTo;

    dispatch(
        accountsActions.fetchLatestMovements({
            selectedAccount: account,
            latestMovementsPageNumber: latestMovementsPageNumber ?? "1",
            pendingMovementsPageNumber: pendingMovementsPageNumber ?? "1",
            findBy: findBy || "period",
            lastDateFrom: resolvedLastDates ? resolvedLastDates[0] : resolvedLastDateFrom,
            lastDateTo: resolvedLastDates ? resolvedLastDates[1] : resolvedLastDateTo,
            pendingDateFrom: resolvedPendingDates ? resolvedPendingDates[0] : resolvedPendingDateFrom,
            pendingDateTo: resolvedPendingDates ? resolvedPendingDates[1] : resolvedPendingDateTo,
            amountFrom: filterPayload.amountFrom ?? null,
            amountTo: filterPayload.amountTo ?? null,
            voucher: filterPayload.voucher ?? null,
            detail: filterPayload.detail ?? null,
            longDetail: filterPayload.longDetail,
            type: filterPayload.type ?? null,
            isLatestMovementsSelected,
            isDesktop,
        }),
    );
}

export function dispatchDownloadLatestMovements(dispatch, accountsActions, params) {
    const {
        account,
        formValues = {},
        isLatestMovementsSelected,
        latestMovementsPageNumber = "1",
        pendingMovementsPageNumber = "1",
        format,
    } = params;

    const {
        lastDateFrom,
        lastDateTo,
        lastDates,
        pendingDateFrom,
        pendingDateTo,
        pendingDates,
        amountFrom,
        amountTo,
        voucher,
        detail,
    } = formValues;

    const findBy = resolveFindByFromFormValues(formValues);
    const movementType = resolveMovementTypeFromFormValues(formValues);
    const type = movementType > 1 ? null : movementType;
    const defaultDates = buildInitialMovementDates();
    const enableRangeDatefield = config.getBoolean("frontend.show.RangeDatePicker.functionalities", false);

    const resolvedLastDates = lastDates ?? defaultDates.lastDates;
    const resolvedPendingDates = pendingDates ?? defaultDates.pendingDates;
    const resolvedLastDateFrom = lastDateFrom ?? defaultDates.lastDateFrom;
    const resolvedLastDateTo = lastDateTo ?? defaultDates.lastDateTo;
    const resolvedPendingDateFrom = pendingDateFrom ?? defaultDates.pendingDateFrom;
    const resolvedPendingDateTo = pendingDateTo ?? defaultDates.pendingDateTo;

    const isLatestSelected = isLatestMovementsSelected ?? formValues.activeSection !== "pendingmovements";

    dispatch(
        accountsActions.downloadLatestMovements({
            format,
            isLatestMovementsSelected: isLatestSelected,
            latestMovementsPageNumber,
            pendingMovementsPageNumber,
            findBy: findBy || "period",
            ...(enableRangeDatefield
                ? {
                      dateFrom: isLatestSelected ? resolvedLastDates?.[0] : resolvedPendingDates?.[0],
                      dateTo: isLatestSelected ? resolvedLastDates?.[1] : resolvedPendingDates?.[1],
                  }
                : {
                      dateFrom: isLatestSelected ? resolvedLastDateFrom : resolvedPendingDateFrom,
                      dateTo: isLatestSelected ? resolvedLastDateTo : resolvedPendingDateTo,
                  }),
            selectedAccount: account,
            pageNumber: 1,
            amountFrom: amountFieldForApi(amountFrom),
            amountTo: amountFieldForApi(amountTo),
            ...(voucher?.trim() ? { voucher: voucher.trim() } : {}),
            ...(detail?.trim() ? { detail: detail.trim() } : {}),
            ...(type != null ? { type } : {}),
        }),
    );
}
